/**
 * Print an on-screen sheet exactly as it was previewed — on one A4 page.
 *
 * The desks used to print the app's own page with everything but the sheet
 * hidden. That printed the sheet reflowed to the paper's width (so the layout
 * was not the preview's), inside a page still as tall as the whole app (so
 * blank pages followed it), and a table slightly taller than the page broke
 * onto a second sheet.
 *
 * Now the sheet is copied into an offscreen frame with the app's own
 * stylesheets and theme, laid out at the width it was previewed at, measured,
 * and scaled as a whole to fit A4 (domain/printFit.js). The frame is the only
 * thing printed, so there is nothing else to hide and no blank pages.
 * "Save as PDF" in the print dialog gives the same single page as a file.
 *
 * iOS Safari prints the parent page rather than a frame, so on iOS the old
 * in-page print is kept as the fallback.
 */

import { PAGE_MARGIN_MM, planPrint } from '../domain/printFit';

/**
 * A phone previews the sheet squeezed to its screen. Paper gets the desk's
 * layout instead: below this viewport width the sheet is laid out at
 * DESK_LAYOUT_WIDTH, not at the phone's.
 */
const PHONE_VIEWPORT = 768;
const DESK_LAYOUT_WIDTH = 720;

/** Give stylesheets, fonts and data: images a moment before measuring. */
const SETTLE_MS = 120;

const FRAME_ID = 'capsule-print-frame';

const isIos = () =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

/** The stylesheets of the app, as markup the frame can load. */
function stylesheetMarkup() {
  return Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map((node) => node.outerHTML)
    .join('\n');
}

/** The sheet as markup, without the parts that never print. */
function sheetMarkup(element) {
  const clone = element.cloneNode(true);
  clone.querySelectorAll('.no-print, [data-print-hide]').forEach((node) => node.remove());
  clone.classList.remove('print-area');
  // A <canvas> does not survive cloning; carry its picture over as an image.
  const sourceCanvases = element.querySelectorAll('canvas');
  clone.querySelectorAll('canvas').forEach((canvas, i) => {
    try {
      const img = document.createElement('img');
      img.src = sourceCanvases[i].toDataURL('image/png');
      img.style.width = `${sourceCanvases[i].clientWidth}px`;
      canvas.replaceWith(img);
    } catch {
      canvas.remove();
    }
  });
  return clone.outerHTML;
}

/** The <html> attributes that carry the theme: data-theme, its mode, the colour scheme. */
function rootAttributes() {
  const root = document.documentElement;
  // The inline style only carries the screen's colour scheme; paper is light.
  return Array.from(root.attributes)
    .filter((attr) => attr.name !== 'style')
    .map((attr) => `${attr.name}="${String(attr.value).replace(/"/g, '&quot;')}"`)
    .join(' ');
}

function frameDocument(element, { title }) {
  const onScreen = Math.ceil(element.getBoundingClientRect().width || 0);
  const width = window.innerWidth < PHONE_VIEWPORT || !onScreen ? Math.max(onScreen, DESK_LAYOUT_WIDTH) : onScreen;
  return `<!doctype html>
<html ${rootAttributes()}>
<head>
<meta charset="utf-8">
<title>${(title || document.title || 'Capsule').replace(/</g, '&lt;')}</title>
${stylesheetMarkup()}
<style id="print-frame-base">
  html { color-scheme: light; }
  html, body { margin: 0 !important; padding: 0 !important; background: #ffffff !important; min-height: 0 !important; height: auto !important; overflow: visible !important; }
  body::before, body::after { display: none !important; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  .print-frame__fit { width: ${width}px; margin: 0 auto; }
  .print-frame__fit > * { max-height: none !important; overflow: visible !important; margin-top: 0 !important; margin-bottom: 0 !important; box-shadow: none !important; }
  .print-frame__fit *, .print-frame__fit *::before, .print-frame__fit *::after { animation: none !important; transition: none !important; }
</style>
<style id="print-frame-page"></style>
</head>
<body><div class="print-frame__fit">${sheetMarkup(element)}</div></body>
</html>`;
}

/** Resolves once every stylesheet and image in the frame has loaded (or failed). */
function settled(doc) {
  const waits = [];
  doc.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
    if (link.sheet) return;
    waits.push(new Promise((resolve) => { link.addEventListener('load', resolve, { once: true }); link.addEventListener('error', resolve, { once: true }); }));
  });
  doc.querySelectorAll('img').forEach((img) => {
    if (img.complete) return;
    waits.push(new Promise((resolve) => { img.addEventListener('load', resolve, { once: true }); img.addEventListener('error', resolve, { once: true }); }));
  });
  if (doc.fonts && doc.fonts.ready) waits.push(doc.fonts.ready.catch(() => {}));
  const timeout = new Promise((resolve) => setTimeout(resolve, 4000));
  return Promise.race([Promise.all(waits), timeout]).then(() => new Promise((resolve) => setTimeout(resolve, SETTLE_MS)));
}

function supportsZoom(win) {
  try {
    return Boolean(win.CSS && win.CSS.supports && win.CSS.supports('zoom', '0.5'));
  } catch {
    return false;
  }
}

/** Scale the copied sheet and size the paper to it. */
function fitToPaper(win, { fit, orientation }) {
  const doc = win.document;
  const box = doc.querySelector('.print-frame__fit');
  const size = { width: box.scrollWidth, height: box.scrollHeight };
  const plan = planPrint(size, { fit, orientation });
  if (plan.scale !== 1) {
    if (supportsZoom(win)) {
      box.style.zoom = String(plan.scale);
    } else {
      box.style.transform = `scale(${plan.scale})`;
      box.style.transformOrigin = 'top center';
      box.style.height = `${size.height * plan.scale}px`;
    }
  }
  doc.getElementById('print-frame-page').textContent =
    `@page { size: A4 ${plan.orientation}; margin: ${PAGE_MARGIN_MM}mm; }` +
    (plan.onePage ? ' html, body { height: auto !important; } .print-frame__fit { break-inside: avoid; page-break-inside: avoid; }' : '');
  return plan;
}

/** The old way: the app's page, everything but the sheet hidden. Kept for iOS. */
function printInPage(element) {
  element.classList.add('print-area');
  document.body.classList.add('printing-area');
  const cleanup = () => {
    element.classList.remove('print-area');
    document.body.classList.remove('printing-area');
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  window.print();
  setTimeout(cleanup, 1500);
}

/**
 * Print `element` as previewed.
 *
 * @param {HTMLElement} element  the sheet on screen
 * @param {object} [options]
 * @param {'page'|'width'} [options.fit]  'page' (default): one page when readable;
 *        'width': page width only — for documents with their own page breaks
 * @param {'auto'|'portrait'|'landscape'} [options.orientation]
 * @param {string} [options.title]  the PDF's file name in "Save as PDF"
 * @returns {Promise<object|null>} the plan printed with, or null when nothing was printed
 */
export function printSheet(element, { fit = 'page', orientation = 'auto', title } = {}) {
  if (!element || typeof document === 'undefined') return Promise.resolve(null);
  if (isIos()) {
    printInPage(element);
    return Promise.resolve(null);
  }

  // One frame, reused: removing a frame while its print dialog is still open
  // cancels the print in some browsers, so the next print rewrites it instead.
  let frame = document.getElementById(FRAME_ID);
  if (!frame) {
    frame = document.createElement('iframe');
    frame.id = FRAME_ID;
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('tabindex', '-1');
    // Offscreen, not display:none — an undisplayed frame does not lay out, and
    // an unlaid-out document prints blank.
    Object.assign(frame.style, { position: 'fixed', top: '0', left: '-12000px', width: '1400px', height: '1000px', border: '0', opacity: '0', pointerEvents: 'none' });
    document.body.appendChild(frame);
  }

  const win = frame.contentWindow;
  const doc = win.document;
  doc.open();
  doc.write(frameDocument(element, { title }));
  doc.close();

  return settled(doc).then(() => {
    const plan = fitToPaper(win, { fit, orientation });
    win.focus();
    win.print();
    return plan;
  });
}

export default printSheet;
